// deps
import React, { memo, useCallback, useMemo, useState } from "react";

// internal deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { default as ArrowRight } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";
import CouponCodeList from "@cart-ui/components/savings-ui-renderer/components/coupon-code-list/CouponCodeList";
import { AriaHidden, ElementObserver, ErrorBoundary, NewTag, replaceText } from "@swiggy-private/core-ui";
import { SavingsLineItemProps, SavingsRightSectionType } from "@swiggy-private/cart-types";
import { OneBlckPlanRemoveConfirmationBottomsheet } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne";
import { PlanPurchaseConfirmationProps } from "@swiggy-private/dls-web-recipe-common-web/dist/components/SwiggyOne";
import { FooterCommsStrip } from "@cart-ui/components/savings-ui-renderer/components/footer-comms-savings-line-item";
import { SAVINGS_LINE_ITEM_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { isEmpty } from "@swiggy-private/js-utils";
import SavingsTooltip from "@cart-ui/components/savings-ui-renderer/components/tooltip/SavingsTooltip";

// styles
import {
    BottomTextContainer,
    Container,
    ImageContainer,
    LabelContainer,
    OuterContainer,
    SubtitleContainer,
    SubtitleIconWrapper,
    TextWrapper,
    TitleContainer,
    Subtitle,
    GenericIconWrapper,
    TooltipContainer,
} from "./SavingsLineItem.style";
import SavingsLineItemRightSection from "./SavingsLineItemRightSection";

// helpers
import { getSavingsLineItemAccessibilityTexts } from "@cart-ui/helpers/cartTwoDotO";
import { isSwiggyBlack, PlanRemoveConfirmationBottomsheetWrapper } from "@swiggy-private/super-ui";

// constants
import { LOGO_DIMENSION } from "@cart-ui/constants";
import { isPopFlow } from "@swiggy-private/checkout-core";
import { UX4_POP_TEXT_PRIMARY } from "@cart-ui/constants/Color";
import { SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE } from "@cart-ui/constants/RenderingDetails";

const SavingsLineItem = ({ SavingsLineItemData, removePlanMessage }: SavingsLineItemProps) => {
    const {
        title,
        subtitle,
        icon,
        onLoad,
        rightSection,
        bottomText,
        onLineItemClick,
        bottomSheetProps,
        showRemovePlanConfirmation,
        planDetails,
        footerCommsStrip,
        footerType = SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.FOOTER_COMMS,
        couponCodeList = null,
    } = SavingsLineItemData;

    const [hasSentImpressionEvent, setHasSentImpressionEvent] = useState(false);
    const isPopCart = isPopFlow();

    const titleValue = useMemo(() => {
        return !isEmpty(title?.titleNode) ? title?.titleNode : title?.value || "";
    }, [title.titleNode, title.value]);

    const hasNoSubtitle = useMemo(() => {
        return !isEmpty(subtitle?.subtitleNode) && !isEmpty(subtitle?.value);
    }, [subtitle?.subtitleNode, subtitle?.value]);

    const isSwiggyBlackPlan = useMemo(() => !isEmpty(planDetails) && isSwiggyBlack(planDetails), [planDetails]);

    const { titleText, subtitleText, rightSectionText } = useMemo(
        () =>
            getSavingsLineItemAccessibilityTexts({
                title,
                subtitle: subtitle || {},
                rightSection,
                onLineItemClick,
            }),
        [onLineItemClick, rightSection, subtitle, title],
    );

    const onViewEnter = useCallback(() => {
        if (!hasSentImpressionEvent) {
            onLoad?.();
        }
        setHasSentImpressionEvent(true);
    }, [onLoad, hasSentImpressionEvent, setHasSentImpressionEvent]);

    const PlanRemoveBottomsheet = useMemo(
        () =>
            isSwiggyBlackPlan ? (
                <OneBlckPlanRemoveConfirmationBottomsheet {...(bottomSheetProps as PlanPurchaseConfirmationProps)} />
            ) : (
                <PlanRemoveConfirmationBottomsheetWrapper
                    bottomSheetProps={bottomSheetProps as PlanPurchaseConfirmationProps}
                    removePlanMessage={removePlanMessage}
                    planDetails={planDetails}
                />
            ),
        [bottomSheetProps, isSwiggyBlackPlan, removePlanMessage, planDetails],
    );

    const isContentEmpty = useMemo(() => {
        if (rightSection.type === SavingsRightSectionType.BUTTON) {
            return isEmpty(rightSection.value) && isEmpty(titleValue); // nudge should not render when title and CTA text is empty in BE response
        }
        if (rightSection.type === SavingsRightSectionType.LABEL) {
            return isEmpty(titleValue); // nudge should not render when title is empty
        }
        return false;
    }, [rightSection.type, rightSection.value, titleValue]);

    const logoDimension = useMemo(
        () => (isSwiggyBlackPlan ? LOGO_DIMENSION.BLACK : LOGO_DIMENSION.ONE),
        [isSwiggyBlackPlan],
    );

    // Determine if right section is a button (to avoid nested buttons)
    const hasRightSectionButton = useMemo(() => {
        return rightSection?.type === SavingsRightSectionType.BUTTON && !!rightSection?.onClick;
    }, [rightSection]);

    // Container should only be button if it has onClick AND no right section button (to avoid nested buttons)
    const isContainerInteractive = useMemo(() => {
        return !!onLineItemClick && !hasRightSectionButton;
    }, [onLineItemClick, hasRightSectionButton]);

    const containerAccessibilityLabel = useMemo(() => {
        if (!isContainerInteractive) return undefined;
        return `${titleText}. ${subtitleText}. ${rightSectionText}`;
    }, [isContainerInteractive, titleText, subtitleText, rightSectionText]);

    if (isContentEmpty) {
        return null;
    }

    return (
        <ErrorBoundary>
            <ElementObserver onViewEnter={onViewEnter}>
                <>
                    <OuterContainer data-testid={SAVINGS_LINE_ITEM_TEST_IDS.CONTAINER}>
                        <Container
                            as={isContainerInteractive ? "button" : "div"}
                            onClick={onLineItemClick}
                            hasNoSubtitle={hasNoSubtitle}
                            {...(isContainerInteractive && { type: "button" })}
                            aria-label={containerAccessibilityLabel}
                        >
                            <div>
                                <TitleContainer data-testid={SAVINGS_LINE_ITEM_TEST_IDS.TITLE}>
                                    <AriaHidden as="div">
                                        <ImageContainer
                                            isBlackUser={isSwiggyBlackPlan}
                                            data-testid={SAVINGS_LINE_ITEM_TEST_IDS.ICON}
                                        >
                                            <Image cloudinaryId={icon} width={logoDimension} height={logoDimension} />
                                        </ImageContainer>
                                    </AriaHidden>

                                    <TextWrapper
                                        variant="V2.body2.neutral.medium"
                                        color="text_High_Emphasis"
                                        onClick={title?.onClick}
                                        maxLines={2}
                                        aria-label={titleText}
                                    >
                                        {titleValue}
                                    </TextWrapper>
                                    {title?.showNewTag && <NewTag skipShimmer />}
                                </TitleContainer>

                                <SubtitleContainer
                                    onClick={subtitle?.onClick}
                                    data-testid={SAVINGS_LINE_ITEM_TEST_IDS.SUBTITLE}
                                >
                                    {subtitle?.subtitleNode ? (
                                        <div>
                                            <Text
                                                variant="V2.body3.neutral.regular"
                                                color="text_Low_Emphasis"
                                                maxLines={1}
                                            >
                                                {replaceText(subtitle?.value || "")}
                                            </Text>
                                        </div>
                                    ) : (
                                        <>
                                            <div>
                                                <Subtitle
                                                    variant="V2.body3.neutral.regular"
                                                    color="text_Low_Emphasis"
                                                    maxLines={2}
                                                    aria-label={subtitleText}
                                                >
                                                    {replaceText(subtitle?.value || "")}
                                                </Subtitle>
                                                {subtitle?.icon && (
                                                    <SubtitleIconWrapper>
                                                        <GenericIconWrapper
                                                            Icon={ArrowRight}
                                                            fillColor="text_Low_Emphasis"
                                                        />
                                                    </SubtitleIconWrapper>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </SubtitleContainer>

                                {showRemovePlanConfirmation && PlanRemoveBottomsheet}
                            </div>
                            <LabelContainer
                                as={hasRightSectionButton ? "button" : "div"}
                                onClick={rightSection?.onClick}
                                data-testid={SAVINGS_LINE_ITEM_TEST_IDS.RIGHT_SECTION}
                                aria-label={hasRightSectionButton ? rightSectionText : undefined}
                                {...(hasRightSectionButton && { type: "button" })}
                            >
                                <SavingsLineItemRightSection
                                    rightSection={{
                                        ...rightSection,
                                        color: isPopCart ? UX4_POP_TEXT_PRIMARY : rightSection?.color,
                                    }}
                                />
                            </LabelContainer>
                        </Container>

                        {!isEmpty(bottomText) && (
                            <BottomTextContainer data-testid={SAVINGS_LINE_ITEM_TEST_IDS.BOTTOM_TEXT}>
                                <Text variant="V2.overline.neutral.medium" color="text_Med_Emphasis" maxLines={1}>
                                    {replaceText(bottomText)}
                                </Text>
                            </BottomTextContainer>
                        )}
                        {!isEmpty(footerCommsStrip) &&
                            footerType === SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.FOOTER_COMMS && (
                                <FooterCommsStrip data={footerCommsStrip} />
                            )}

                        {!isEmpty(footerCommsStrip) && footerType === SAVINGS_LINE_ITEM_BOTTOM_SHEET_TYPE.TOOLTIP && (
                            <TooltipContainer data-testid={SAVINGS_LINE_ITEM_TEST_IDS.TOOLTIP}>
                                <SavingsTooltip footerCommsStrip={footerCommsStrip} onLoad={onLoad} />
                            </TooltipContainer>
                        )}
                    </OuterContainer>
                    {!isEmpty(couponCodeList?.codes) && <CouponCodeList {...couponCodeList} />}
                </>
            </ElementObserver>
        </ErrorBoundary>
    );
};

export default memo(SavingsLineItem);
